// CTSK-509 — the list + "Add a note" body shared by the document / work-item
// panel and the standalone Notes page. The caller has already checked the gate
// (useNotesAccess); this component only renders and mutates.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NOTE_LIST_MAX, type Note, type NoteCreateInput, type NoteGridType } from '@captain-tasko/shared';
import { ApiError, apiSingle } from '../../lib/api.js';
import type { NotesAccess } from '../../hooks/useNotesAccess.js';
import { NoteCard } from './NoteCard.js';
import { NoteEditor } from './NoteEditor.js';
import { NotesWall, type DropAnchor } from './NotesWall.js';
import { useNoteMutations, useNotesList, type NotesListParams } from './useNotes.js';
import './notes.css';

export interface NotesSectionProps {
  params: NotesListParams;
  access: NotesAccess;
  /** Attach new notes to this target; omit for standalone notes. */
  target?: { targetType: 'document' | 'workitem'; targetId: string };
  /** Standalone page: show "Load more" instead of the "latest 100" hint. */
  paged?: boolean;
  emptyText?: string;
  onNavigate?: (path: string) => void;
  /** Called with the number of notes after each list change (panel badge). */
  onCount?: (count: number) => void;
  /** CTSK-514 (paged wall only): grid type, permalink target, order-change hook. */
  gridType?: NoteGridType;
  focusId?: string | null;
  /** Called after a drag-reorder was saved, so the page can switch to Manual. */
  onReordered?: () => void;
}

function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : 'Something went wrong';
}

export function NotesSection({ params, access, target, paged = false, emptyText, onNavigate, onCount, gridType = 'masonry', focusId = null, onReordered }: NotesSectionProps) {
  const list = useNotesList(params, access.enabled);
  const { create, update, remove, reorder } = useNoteMutations(params);
  const [adding, setAdding] = useState(false);
  const error = create.error ?? update.error ?? remove.error ?? reorder.error;
  const count = list.notes.length;
  const sortedView = 'ordering' in params && params.ordering?.mode === 'sorted';

  // Permalink (/notes/:id): fetch the note itself so an attached note, a note on
  // a page that is not loaded yet, and a missing/forbidden note are all handled.
  const focus = useQuery({
    queryKey: ['notes', 'one', focusId],
    queryFn: () => apiSingle<Note>(`/notes/${focusId}`),
    enabled: paged && Boolean(focusId) && access.enabled,
    retry: 0,
  });
  const onWall = focusId ? list.notes.some((n) => n.id === focusId) : false;
  const showFocusCard = Boolean(focus.data) && !onWall && !list.isLoading;

  function reorderNote(noteId: string, anchor: DropAnchor, displayed: string[]) {
    reorder.mutate(
      { noteId, ...anchor, ...(sortedView ? { basis: displayed } : {}) },
      { onSuccess: () => onReordered?.() },
    );
  }
  // Effect, not render: onCount sets state in the parent panel.
  useEffect(() => { onCount?.(count); }, [count, onCount]);

  const composer = adding ? (
    <NoteEditor
      saveLabel="Add note"
      isSaving={create.isPending}
      error={create.isError ? message(create.error) : null}
      onCancel={() => { setAdding(false); create.reset(); }}
      onSave={(draft) => {
        const input: NoteCreateInput = { body: draft.body, color: draft.color, ...target };
        create.mutate(input, { onSuccess: () => setAdding(false) });
      }}
    />
  ) : null;

  return (
    <div>
      {list.isError && <div role="alert" className="note-editor__error">Could not load notes.</div>}
      {paged && (
        <div className="notes-compose">
          {access.canCreate && !adding && (
            <button type="button" className="btn" onClick={() => setAdding(true)}>Add a note</button>
          )}
          {composer}
        </div>
      )}
      {paged && focusId && focus.isError && (
        <div role="status" className="notes-panel__hint" data-testid="note-not-found">
          This note was deleted or you can&apos;t see it.
        </div>
      )}
      {paged && showFocusCard && focus.data && (
        <div className="notes-focus" data-testid="note-focus">
          <NoteCard
            note={focus.data}
            currentUserId={access.userId}
            canEdit={access.canEdit}
            canDelete={access.canDelete}
            onNavigate={onNavigate}
            onUpdate={(id, patch) => update.mutate({ id, patch })}
            onDelete={(id) => remove.mutate(id)}
            onResize={(id, heightPx) => update.mutate({ id, patch: { heightPx } })}
            highlighted
          />
        </div>
      )}
      {paged ? (
        <NotesWall
          notes={list.notes}
          gridType={gridType}
          currentUserId={access.userId}
          canEdit={access.canEdit}
          canDelete={access.canDelete}
          onNavigate={onNavigate}
          onUpdate={(id, patch) => update.mutate({ id, patch })}
          onDelete={(id) => remove.mutate(id)}
          onResize={(id, heightPx) => update.mutate({ id, patch: { heightPx } })}
          onReorder={access.canEdit ? reorderNote : undefined}
          highlightId={focusId}
        />
      ) : (
        <div className="notes-list">
          {list.notes.map((n) => (
            <NoteCard
              key={n.id}
              note={n}
              currentUserId={access.userId}
              canEdit={access.canEdit}
              canDelete={access.canDelete}
              onNavigate={onNavigate}
              onUpdate={(id, patch) => update.mutate({ id, patch })}
              onDelete={(id) => remove.mutate(id)}
              onResize={(id, heightPx) => update.mutate({ id, patch: { heightPx } })}
            />
          ))}
          {composer}
        </div>
      )}
      {!list.isLoading && !list.isError && list.notes.length === 0 && !adding && (
        <div className="notes-panel__hint">{emptyText ?? 'No notes yet.'}</div>
      )}
      {(update.isError || remove.isError || reorder.isError) && (
        <div role="alert" className="note-editor__error">{message(error)}</div>
      )}
      <div className="notes-panel__actions">
        {!paged && access.canCreate && !adding && (
          <button type="button" className="btn" onClick={() => setAdding(true)}>Add a note</button>
        )}
        {paged && list.hasMore && (
          <button type="button" className="btn btn--ghost" disabled={list.isFetchingMore} onClick={list.fetchMore}>
            {list.isFetchingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
        {!paged && list.notes.length >= NOTE_LIST_MAX && (
          <span className="notes-panel__hint">Showing the latest {NOTE_LIST_MAX} notes.</span>
        )}
      </div>
    </div>
  );
}
